/**
 * Country to Language Mapping for Automatic Language Selection
 * Maps country codes to supported language codes
 */

import { Language } from '@/contexts/language/types';

export const countryToLanguageMap: Record<string, Language> = {
  // French-speaking countries
  'FR': 'fr', // France
  'BE': 'fr', // Belgium (multilingual, but French is widely supported)
  
  // All other countries default to English
  // Including: ES, RO, PL, DE, IT, NL, AT, HU, BG, HR, CZ, DK, EE, FI, GR, IE, LV, LT, LU, MT, PT, SK, SI, SE, CY
};

/**
 * Determines the appropriate language based on country code
 * @param countryCode - ISO 2-letter country code (e.g., 'FR', 'RO')
 * @returns Language code ('en', 'fr', 'es', 'ro', 'pl')
 */
export const getLanguageFromCountry = (countryCode: string): Language => {
  return countryToLanguageMap[countryCode.toUpperCase()] || 'en';
};

/**
 * Get the country name for a given country code
 * @param countryCode - ISO 2-letter country code
 * @returns Human-readable country name
 */
export const getCountryName = (countryCode: string): string => {
  const countryNames: Record<string, string> = {
    'RO': 'Romania',
    'FR': 'France', 
    'DE': 'Germany',
    'IT': 'Italy',
    'ES': 'Spain',
    'PL': 'Poland',
    'NL': 'Netherlands',
    'BE': 'Belgium',
    'AT': 'Austria',
    'HU': 'Hungary',
    'BG': 'Bulgaria',
    'HR': 'Croatia',
    'CZ': 'Czech Republic',
    'DK': 'Denmark',
    'EE': 'Estonia',
    'FI': 'Finland',
    'GR': 'Greece',
    'IE': 'Ireland',
    'LV': 'Latvia',
    'LT': 'Lithuania',
    'LU': 'Luxembourg',
    'MT': 'Malta',
    'PT': 'Portugal',
    'SK': 'Slovakia',
    'SI': 'Slovenia',
    'SE': 'Sweden',
    'CY': 'Cyprus'
  };
  
  return countryNames[countryCode.toUpperCase()] || countryCode;
};

/**
 * Get supported countries that have automatic language detection
 * @returns Array of country codes that have language mappings
 */
export const getSupportedCountriesWithLanguages = (): string[] => {
  return Object.keys(countryToLanguageMap);
};